import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { NOTIFICATIONS_MAP, notify } from '../../../shared/features/notifications/ps-notifications';
import { isMobileUa } from '../../../shared/features/uatraits/selectors';
import { copy } from '../../../shared/helpers/clipboard';
import { i18n } from '../../../shared/helpers/i18n';
import { countMetrika } from '../../../shared/helpers/metrika';
import { shareCallAction } from '../features/share-link/slice';
import {
    getConferenceId,
    getConferenceShareLink,
    getFormattedCallNumber,
    selectConferencePhones,
    selectIsConferencePhoneAvail,
    selectMeetingName,
} from '../selectors';
import { ConferencePhone, NotificationId } from '../types';

/**
 * Хук для работы с данными конференции через буфер обмена
 */
export function useShareCallData() {
    const fullCallNumber = useSelector(getFormattedCallNumber);
    const isMobile = useSelector(isMobileUa);
    const dispatch = useDispatch();
    const conferencePhones = useSelector(selectConferencePhones);
    const isConferencePhoneAvail = useSelector(selectIsConferencePhoneAvail);
    const conferenceShareLink = useSelector(getConferenceShareLink);
    const meetingName = useSelector(selectMeetingName);
    const conferenceId = useSelector(getConferenceId);
    let displayingCallNumber = fullCallNumber;

    if (displayingCallNumber.length > 17) {
        displayingCallNumber = `${fullCallNumber.slice(0, 17)}...`;
    }

    const copyConferenceUrlToClipboard = useCallback(() => {
        dispatch(shareCallAction());
    }, [dispatch]);

    const copyCallNumberToClipboard = useCallback(() => {
        return copy(fullCallNumber, isMobile);
    }, [fullCallNumber, isMobile]);

    const copyPhoneToClipboard = useCallback(
        async (phone: ConferencePhone) => {
            const message = `${i18n('call', 'phone-invitation-header')}\n${i18n('call', 'phone-template', {
                phone: phone.number,
                number: phone.extension,
            })}`;
            const isSuccess = await copy(message, true);
            if (isSuccess) {
                countMetrika('meeting_screen', 'share_meeting', 'share_by_phone_num', conferenceId);
                dispatch(notify(NOTIFICATIONS_MAP[NotificationId.PHONE_COPIED]()));
            } else {
                countMetrika('meeting_screen', 'share_meeting', 'share_by_phone_num', 'share_error', conferenceId);
            }
            return isSuccess;
        },
        [conferenceId, dispatch]
    );

    const copyInvitationToClipboard = useCallback(async () => {
        const messageParts = [i18n('call', 'invitation-header')];
        if (meetingName) {
            messageParts.push(`«${meetingName}»`);
        }
        messageParts.push(`\n${i18n('call', 'meeting-link')}`);
        messageParts.push(`${conferenceShareLink}`);
        if (isConferencePhoneAvail) {
            messageParts.push(`\n${i18n('call', 'call-into-meeting')}`);
            conferencePhones?.forEach((conferencePhone) => {
                messageParts.push(
                    `${i18n('call', 'phone-template', {
                        phone: conferencePhone.number,
                        number: conferencePhone.extension,
                    })}`
                );
            });
        }
        const message = messageParts.join('\n');
        const isSuccess = await copy(message, true);
        if (isSuccess) {
            countMetrika('meeting_screen', 'share_meeting', 'share_by_invite', conferenceId);
            dispatch(notify(NOTIFICATIONS_MAP[NotificationId.INVITATION_COPIED]()));
        } else {
            countMetrika('meeting_screen', 'share_meeting', 'share_by_invite', 'share_error', conferenceId);
        }
        return isSuccess;
    }, [conferenceId, conferencePhones, conferenceShareLink, dispatch, isConferencePhoneAvail, meetingName]);

    return {
        callNumber: displayingCallNumber,
        copyConferenceUrlToClipboard,
        copyCallNumberToClipboard,
        copyPhoneToClipboard,
        copyInvitationToClipboard,
    };
}
